
export default function DesignDecisions() {
  const decisions = [
    {
      context: 'Site workers needed to complete safety inspections and incident reports in environments with unreliable internet connectivity. Losing data mid-inspection created safety risks and compliance gaps.',
      decision: 'Offline-First Form Architecture',
      rationale: 'We designed the form builder module with local-first data persistence. Forms are cached on device, submissions are queued locally, and sync automatically when connectivity is restored.',
      impact: 'Site workers can now complete inspections without connectivity concerns. Incident reporting completion rates increased significantly.',
      icon: 'ri-wifi-off-line',
      color: '#F16051'
    },
    {
      context: 'Different user roles required vastly different levels of access and functionality. A one-size-fits-all approach would either overwhelm basic users or limit power users.',
      decision: 'Role-Based Interface Adaptation',
      rationale: 'Rather than building separate interfaces for each role, we designed a single adaptive interface that progressively reveals functionality based on user permissions.',
      impact: 'This approach reduced development complexity while providing tailored experiences. Users see only what they need.',
      icon: 'ri-user-settings-line',
      color: '#182423'
    },
    {
      context: 'Construction projects require extensive documentation with strict version control and approval workflows. Teams struggled with document sprawl and outdated versions.',
      decision: 'Unified Document Lifecycle Management',
      rationale: 'We integrated document management directly into each module rather than treating it as a separate system. Documents are contextually linked to projects, ITPs, and ITCs.',
      impact: 'Teams always access the correct document version. Approval bottlenecks were reduced through automated routing.',
      icon: 'ri-file-shield-2-line',
      color: '#F16051'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: '#F16051', color: 'white' }}>
            Design Thinking
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#182423' }}>
            Key Design Decisions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Critical decisions that shaped the core UX strategy and had significant impact on user adoption
          </p>
        </div>

        <div className="space-y-8">
          {decisions.map((decision, index) => (
            <div key={index} className="group relative">
              {/* Main Card */}
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden">
                {/* Header */}
                <div className="p-8 border-b border-gray-100" style={{ background: `linear-gradient(135deg, ${decision.color}10 0%, transparent 100%)` }}>
                  <div className="flex items-center gap-6">
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: decision.color }}
                    >
                      <i className={`${decision.icon} text-white text-3xl`}></i>
                    </div>
                    <div>
                      <span className="text-sm font-bold uppercase tracking-wider" style={{ color: decision.color }}>
                        Decision {index + 1}
                      </span>
                      <h3 className="text-2xl font-bold text-gray-900">{decision.decision}</h3>
                    </div>
                  </div>
                </div>

                {/* Content Grid */}
                <div className="grid md:grid-cols-2 gap-0">
                  {/* Left Column */}
                  <div className="p-8 border-r border-gray-100">
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: decision.color }}></div>
                        <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400">Context</h4>
                      </div>
                      <p className="text-gray-600 leading-relaxed">{decision.context}</p>
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: decision.color }}></div>
                        <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400">Rationale</h4>
                      </div>
                      <p className="text-gray-600 leading-relaxed">{decision.rationale}</p>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="p-8 flex flex-col">
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: decision.color }}></div>
                        <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400">Impact</h4>
                      </div>
                      <p className="text-gray-600 leading-relaxed">{decision.impact}</p>
                    </div>
                    
                    {/* Image Placeholder */}
                    <div className="flex-1 bg-gray-50 rounded-2xl p-6 flex items-center justify-center min-h-[120px]">
                      <div className="text-center">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2" style={{ backgroundColor: `${decision.color}20` }}>
                          <i className="ri-image-line text-xl" style={{ color: decision.color }}></i>
                        </div>
                        <p className="text-sm text-gray-400">Visual placeholder</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
